/**
 * App.tsx — Performance-first routing
 *
 * ALL page-level components are lazily loaded so the initial JS bundle
 * only contains React core + routing. Each route chunk downloads only
 * when the user navigates to that URL, cutting initial bundle by ~70%
 * and reducing LCP / TBT dramatically.
 */
import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import { normalizePath } from "./utils/url.util";
import "./styles.scss";

// ── Always-visible shell (NOT lazy — needed on first paint) ─────────────────
import Navbar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";
import ParentPrivateRoute from "./components/ParentPrivateRoute";
import PageViewTracker from "./components/PageViewTracker";

// ── Static data (small arrays, OK to import eagerly) ───────────────────────
import courses from "./data/coursesData";
import { authorList } from "./data/authorData";
import GoogleTagManager from "./GoogleTagManager";

// ── Page-level lazy chunks ─────────────────────────────────────────────────
const Home = React.lazy(
  () => import(/* webpackChunkName: "home" */ "./pages/Home"),
);
const About = React.lazy(
  () => import(/* webpackChunkName: "about" */ "./pages/About"),
);
const Contact = React.lazy(
  () => import(/* webpackChunkName: "contact" */ "./pages/Contact"),
);
const NotFound = React.lazy(
  () => import(/* webpackChunkName: "not-found" */ "./pages/NotFound"),
);
const Courses = React.lazy(
  () => import(/* webpackChunkName: "courses" */ "./pages/Courses"),
);
const CourseDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "course-details" */ "./components/CourseDetails"
    ),
);
const CartPage = React.lazy(
  () => import(/* webpackChunkName: "cart" */ "./pages/cart.page"),
);
const Login = React.lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/authentication/Login"),
);
const Signup = React.lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/authentication/Signup"),
);
const MentorSignup = React.lazy(
  () =>
    import(
      /* webpackChunkName: "auth" */ "./pages/authentication/MentorSignup"
    ),
);
const CampusCounselorSignup = React.lazy(
  () =>
    import(
      /* webpackChunkName: "auth" */ "./pages/authentication/CampusCounselorSignup"
    ),
);
const Profile = React.lazy(
  () => import(/* webpackChunkName: "profile" */ "./pages/profile/Profile"),
);
const CareerPage = React.lazy(
  () => import(/* webpackChunkName: "career" */ "./pages/CareerPage"),
);
const OfflineClass = React.lazy(
  () =>
    import(/* webpackChunkName: "services" */ "./pages/services/OfflineClass"),
);
const OnlineClass = React.lazy(
  () =>
    import(/* webpackChunkName: "services" */ "./pages/services/OnlineClass"),
);
const ProjectRequirement = React.lazy(
  () =>
    import(
      /* webpackChunkName: "services" */ "./pages/services/ProjectRequirement"
    ),
);
const RoadmapPage = React.lazy(
  () => import(/* webpackChunkName: "roadmap" */ "./pages/roadmap/RoadmapPage"),
);
const RoadmapStepPage = React.lazy(
  () =>
    import(/* webpackChunkName: "roadmap" */ "./pages/roadmap/RoadmapStepPage"),
);
const PrivacyPolicy = React.lazy(
  () => import(/* webpackChunkName: "legal" */ "./pages/legal/PrivacyPolicy"),
);
const TermsAndConditions = React.lazy(
  () =>
    import(/* webpackChunkName: "legal" */ "./pages/legal/TermsAndConditions"),
);
const CancellationAndRefund = React.lazy(
  () =>
    import(
      /* webpackChunkName: "legal" */ "./pages/legal/CancellationAndRefund"
    ),
);
const ShippingAndDelivery = React.lazy(
  () =>
    import(/* webpackChunkName: "legal" */ "./pages/legal/ShippingAndDelivery"),
);
const TutorialsPage = React.lazy(
  () =>
    import(/* webpackChunkName: "tutorials" */ "./pages/tutorials/tutorials"),
);
const TutorialsDetails = React.lazy(
  () =>
    import(/* webpackChunkName: "tutorials" */ "./components/TutorialsDetails"),
);
const Mentor = React.lazy(
  () => import(/* webpackChunkName: "mentors" */ "./pages/Mentor"),
);
const AuthorDetails = React.lazy(
  () => import(/* webpackChunkName: "mentors" */ "./components/AuthorDetails"),
);
const AuthorsPage = React.lazy(
  () => import(/* webpackChunkName: "mentors" */ "./pages/authors/AuthorsPage"),
);
const MentorEarningCalculatorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "mentors" */ "./pages/MentorEarningCalculatorPage"
    ),
);
const Search = React.lazy(
  () => import(/* webpackChunkName: "search" */ "./pages/search/Search"),
);
const InterviewsList = React.lazy(
  () =>
    import(
      /* webpackChunkName: "interviews" */ "./pages/interviews/InterviewsList"
    ),
);
const InterviewDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "interviews" */ "./pages/interviews/InterviewDetails"
    ),
);
const InterviewQuestionDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "interviews" */ "./pages/interviews/InterviewQuestionDetails"
    ),
);
const BlogListingPage = React.lazy(
  () => import(/* webpackChunkName: "blog" */ "./pages/blog/BlogListingPage"),
);
const BlogDetailPage = React.lazy(
  () =>
    import(/* webpackChunkName: "blog-detail" */ "./pages/blog/BlogDetailPage"),
);
const GuestBlogWritePage = React.lazy(
  () =>
    import(/* webpackChunkName: "blog" */ "./pages/blog/GuestBlogWritePage"),
);
const BlogSubscriptionPage = React.lazy(
  () =>
    import(/* webpackChunkName: "blog" */ "./pages/blog/BlogSubscriptionPage"),
);
const AdminBlogListPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AdminBlogListPage"
    ),
);
const AdminBlogEditorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AdminBlogEditorPage"
    ),
);
const AdminNewsletterPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AdminNewsletterPage"
    ),
);
const AdminBlogSuggestionsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AdminBlogSuggestionsPage"
    ),
);
const CourseCounseling = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/branding/counseling/course-counseling.page"
    ),
);
const OnlineCourseCounseling = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/branding/counseling/online-course-counseling.page"
    ),
);
const CourseCounselingAfterGraduation = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/branding/counseling/course-counseling-after-graduation.page"
    ),
);
const CourseCounselingAfter12th = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/branding/counseling/course-counseling-after-12th.page"
    ),
);
const EducationCounseling = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/branding/counseling/EducationCounseling"
    ),
);
const CareerCounseling = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/career-counseling/CareerCounseling.page"
    ),
);
const CareerCounselingAfter12th = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/career-counseling/CareerCounselingAfter12th"
    ),
);
const CareerCounselingAfterGraduation = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/career-counseling/CareerCounselingAfterGraduation"
    ),
);
const CareerCounselingCareerChange = React.lazy(
  () =>
    import(
      /* webpackChunkName: "counseling" */ "./pages/career-counseling/CareerCounselingCareerChange"
    ),
);
const CoursesAfterGraduation = React.lazy(
  () =>
    import(
      /* webpackChunkName: "courses-variant" */ "./pages/CoursesAfterGraduation/CoursesAfterGraduation"
    ),
);
const CoursesAfter12th = React.lazy(
  () =>
    import(
      /* webpackChunkName: "courses-variant" */ "./pages/CoursesAfter12th/CoursesAfter12th"
    ),
);
const CareerChangeCourses = React.lazy(
  () =>
    import(
      /* webpackChunkName: "courses-variant" */ "./pages/CareerChangeCourses/CareerChangeCourses"
    ),
);
const CareerRoadmap = React.lazy(
  () =>
    import(
      /* webpackChunkName: "courses-variant" */ "./pages/CareerRoadmap/CareerRoadmap"
    ),
);
const CareerOptionsAfter12th = React.lazy(
  () =>
    import(
      /* webpackChunkName: "courses-variant" */ "./pages/CareerOptionsAfter12th/CareerOptionsAfter12th"
    ),
);
const FreeResources = React.lazy(
  () =>
    import(
      /* webpackChunkName: "free-resources" */ "./pages/FreeResources/FreeResources"
    ),
);
const Products = React.lazy(
  () => import(/* webpackChunkName: "ai-products" */ "./pages/Products"),
);
const AiPlugin = React.lazy(
  () => import(/* webpackChunkName: "ai-products" */ "./pages/AiPlugin"),
);
const Docs = React.lazy(
  () => import(/* webpackChunkName: "ai-products" */ "./pages/Docs"),
);
const AiSalesAssistant = React.lazy(
  () =>
    import(/* webpackChunkName: "ai-products" */ "./pages/AiSalesAssistant"),
);
const AiChatbotForWebsite = React.lazy(
  () =>
    import(/* webpackChunkName: "ai-products" */ "./pages/AiChatbotForWebsite"),
);
const IncreaseWebsiteConversions = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ai-products" */ "./pages/IncreaseWebsiteConversions"
    ),
);
const LifeSkills = React.lazy(
  () => import(/* webpackChunkName: "life-skills" */ "./pages/LifeSkills"),
);
const ImproveCommunicationSkills = React.lazy(
  () =>
    import(
      /* webpackChunkName: "life-skills" */ "./pages/ImproveCommunicationSkills"
    ),
);
const ProgramDetailsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "life-skills-detail" */ "./pages/ProgramDetailsPage"
    ),
);
const LessonPage = React.lazy(
  () =>
    import(/* webpackChunkName: "life-skills-detail" */ "./pages/LessonPage"),
);
const SubscriptionManagementPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "life-skills" */ "./pages/SubscriptionManagementPage"
    ),
);
const ToolsHubPage = React.lazy(
  () =>
    import(/* webpackChunkName: "tools-hub" */ "./pages/tools/ToolsHubPage"),
);
const ToolsUpgradePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tools-upgrade" */ "./pages/tools/ToolsUpgradePage"
    ),
);
const AiCommunicationLabSetupPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ai-communication-lab" */ "./pages/tools/AiCommunicationLabSetupPage"
    ),
);
const AiCommunicationLabRoomPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ai-communication-lab" */ "./pages/tools/AiCommunicationLabRoomPage"
    ),
);
const CommunicationCheckerPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-comm" */ "./pages/tools/CommunicationCheckerPage"
    ),
);
const ConfidenceScoreTestPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-conf" */ "./pages/tools/ConfidenceScoreTestPage"
    ),
);
const EmailWritingAnalyzerPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-email" */ "./pages/tools/EmailWritingAnalyzerPage"
    ),
);
const StressLevelQuizPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-stress" */ "./pages/tools/StressLevelQuizPage"
    ),
);
const IQTestPage = React.lazy(
  () => import(/* webpackChunkName: "tool-iq" */ "./pages/tools/IQTestPage"),
);
const FinancialPlanningSipCalculatorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-sip" */ "./pages/tools/financial-planning/FinancialPlanningSipCalculatorPage"
    ),
);
const SalaryGrowthPredictorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-salary" */ "./pages/tools/salary-growth/SalaryGrowthPredictorPage"
    ),
);
const SitemapPage = React.lazy(
  () => import(/* webpackChunkName: "sitemap" */ "./pages/Sitemap/SitemapPage"),
);
const CareerBlueprintGeneratorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "tool-blueprint" */ "./pages/tools/career-blueprint/CareerBlueprintGeneratorPage"
    ),
);
const GovernmentSchemesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "government-schemes" */ "./pages/government-schemes/GovernmentSchemesPage"
    ),
);
const SchemeDetailsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "government-schemes" */ "./pages/government-schemes/SchemeDetailsPage"
    ),
);
const EligibilityCheckerPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "government-schemes" */ "./pages/government-schemes/EligibilityCheckerPage"
    ),
);
const MyTransactionsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/MyTransactionsPage"
    ),
);
const GovernmentSchemesAdminPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/GovernmentSchemesAdminPage"
    ),
);
const SearchAnalyticsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/SearchAnalyticsPage"
    ),
);
const ActivityDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/ActivityDashboard"
    ),
);
const AddEditGovernmentSchemePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditGovernmentSchemePage"
    ),
);
const AiCommunicationLabAdminPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AiCommunicationLabAdminPage"
    ),
);
const AiCommunicationLabTransactionsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AiCommunicationLabTransactionsPage"
    ),
);
const AiCommunicationLabPage = React.lazy(
  () => import(/* webpackChunkName: "tools" */ "./pages/AiCommunicationLabPage"),
);
const AiCommunicationLabPricingPage = React.lazy(
  () => import(/* webpackChunkName: "tools" */ "./pages/AiCommunicationLabPricingPage"),
);
const FindServices = React.lazy(
  () => import(/* webpackChunkName: "find-services" */ "./pages/FindServices"),
);
const FindServicesList = React.lazy(
  () =>
    import(/* webpackChunkName: "find-services" */ "./pages/FindServicesList"),
);
const ServiceProviderDetail = React.lazy(
  () =>
    import(
      /* webpackChunkName: "find-services" */ "./pages/ServiceProviderDetail.themed"
    ),
);
const ProviderOnboarding = React.lazy(
  () =>
    import(
      /* webpackChunkName: "find-services" */ "./pages/ProviderOnboarding"
    ),
);
const LifeSkillsProgramsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsProgramsPage"
    ),
);
const LifeSkillsCategoriesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsCategoriesPage"
    ),
);
const AddEditLifeSkillsCategoryPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditLifeSkillsCategoryPage"
    ),
);
const LifeSkillsPricingPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsPricingPage"
    ),
);
const AddEditLifeSkillsPricingPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditLifeSkillsPricingPage"
    ),
);
const LifeSkillsActivitiesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsActivitiesPage"
    ),
);
const AddEditLifeSkillsActivityPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditLifeSkillsActivityPage"
    ),
);
const LifeSkillsSubscriptionsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsSubscriptionsPage"
    ),
);
const LifeSkillsClientsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsClientsPage"
    ),
);
const AddEditClientPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditClientPage"
    ),
);
const ClientDetailPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/ClientDetailPage"
    ),
);
const LifeSkillsProgressLogsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsProgressLogsPage"
    ),
);
const LifeSkillsToyCatalogPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsToyCatalogPage"
    ),
);
const AddEditToyCatalogPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditToyCatalogPage"
    ),
);
const LifeSkillsDietPlansPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsDietPlansPage"
    ),
);
const AddEditDietPlanPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditDietPlanPage"
    ),
);
const LifeSkillsPartnerCentersPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/LifeSkillsPartnerCentersPage"
    ),
);
const AddEditPartnerCenterPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditPartnerCenterPage"
    ),
);
const CampusCounselorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "campus" */ "./pages/campus/CampusCounselorPage"
    ),
);
const CounselorDetails = React.lazy(
  () =>
    import(/* webpackChunkName: "campus" */ "./components/CounselorDetails"),
);
const SessionBooking = React.lazy(
  () =>
    import(
      /* webpackChunkName: "campus" */ "./pages/campus/session-booking.page"
    ),
);
const SessionBookedForMe = React.lazy(
  () =>
    import(/* webpackChunkName: "campus" */ "./components/SessionBookedForMe"),
);
const SessionBookedByMe = React.lazy(
  () =>
    import(/* webpackChunkName: "campus" */ "./components/SessionBookedByMe"),
);
const ChatComponent = React.lazy(
  () => import(/* webpackChunkName: "campus" */ "./components/ChatComponent"),
);
const CampusCounselorIntro = React.lazy(
  () =>
    import(
      /* webpackChunkName: "campus" */ "./components/CampusCounselorIntro"
    ),
);
const SessionPricing = React.lazy(
  () =>
    import(/* webpackChunkName: "campus" */ "./pages/campus/SessionPriceing"),
);
const QADetailPage = React.lazy(
  () => import(/* webpackChunkName: "profile" */ "./components/QADetailPage"),
);
const UserCourses = React.lazy(
  () => import(/* webpackChunkName: "profile" */ "./components/UserCourses"),
);
const OpenApi = React.lazy(
  () => import(/* webpackChunkName: "openapi" */ "./pages/api/OpenApi"),
);
const HtmlSitemap = React.lazy(
  () =>
    import(/* webpackChunkName: "sitemap" */ "./pages/HtmlSitemap/HtmlSitemap"),
);
const ParentDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "parent" */ "./pages/parent-portal/ParentDashboard"
    ),
);
const ParentProgressForm = React.lazy(
  () =>
    import(
      /* webpackChunkName: "parent" */ "./pages/parent-portal/ParentProgressForm"
    ),
);
const ParentRecommendations = React.lazy(
  () =>
    import(
      /* webpackChunkName: "parent" */ "./pages/parent-portal/ParentRecommendations"
    ),
);
const Dashboard = React.lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./pages/dashboard/Dashboard"),
);
const AiWidgetPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AiWidgetPage"
    ),
);
const TenantAdminPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/TenantAdminPage"
    ),
);
const CallRequestsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CallRequestPage"
    ),
);
const CourseInqueryPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CourseInqueryPage"
    ),
);
const StudentsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/StudentsPage"
    ),
);
const MentorPage = React.lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./pages/dashboard/MentorsPage"),
);
const CoursesPage = React.lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./pages/dashboard/CoursesPage"),
);
const OrdersPage = React.lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./pages/dashboard/OrdersPage"),
);
const AddEditCoursePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCoursePage"
    ),
);
const TopicsPage = React.lazy(
  () =>
    import(/* webpackChunkName: "dashboard" */ "./pages/dashboard/TopicsPage"),
);
const AddEditTopicPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditTopicPage"
    ),
);
const BulletPointsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/BulletPointsPage"
    ),
);
const AddEditBulletPointPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditBulletPointPage"
    ),
);
const CourseForPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CourseForPage"
    ),
);
const AddEditCourseForPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCourseForPage"
    ),
);
const PerksRewardPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/PerksRewardPage"
    ),
);
const AddEditPerkRewardPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditPerkRewardPage"
    ),
);
const CourseFaqPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CourseFaqPage"
    ),
);
const AddEditCourseFaqPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCourseFaqPage"
    ),
);
const CourseResourcePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CourseResourcePage"
    ),
);
const AddEditCourseResourcesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCourseResourcesPage"
    ),
);
const CoursePrerequisitPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CoursePrerequisitPage"
    ),
);
const AddEditCoursePrerequisitPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCoursePrerequisitPage"
    ),
);
const CourseQuizPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/CourseQuizPage"
    ),
);
const AddEditCourseQuizPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCourseQuizPage"
    ),
);
const InterviewPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/InterviewPage"
    ),
);
const AddEditInterviewPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditInterviewPage"
    ),
);
const InterviewQuestionPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/InterviewQuestionPage"
    ),
);
const AddEditInterviewQuestionPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditInterviewQuestionPage"
    ),
);
const AddEditMentorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditMentorPage"
    ),
);
const AddEditMentorSocialPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditMentorSocialPage"
    ),
);
const AddEditMentorWorkPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditMentorWorkPage"
    ),
);
const AddEditCounselorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCounselorPage"
    ),
);
const AddEditCounselorCollegePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCounselorCollegePage"
    ),
);
const AddEditCounselorSchoolPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCounselorSchoolPage"
    ),
);
const AddEditCounselorPackagePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard" */ "./pages/dashboard/AddEditCounselorPackagePage"
    ),
);
const Footer = React.lazy(
  () => import(/* webpackChunkName: "footer" */ "./components/Footer"),
);
// ── Inject noindex for private/dashboard routes that lack their own Helmet ──
const NOINDEX_PREFIXES = ["/dashboard", "/profile", "/admin"];
const NoIndexGuard: React.FC = () => {
  const { pathname } = useLocation();
  const shouldNoindex = NOINDEX_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
  if (!shouldNoindex) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex,nofollow" />
    </Helmet>
  );
};

// ── Minimal loading skeleton ───────────────────────────────────────────────
const PageSkeleton: React.FC = () => (
  <div
    style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#888",
    }}
  >
    <span>Loading…</span>
  </div>
);

// Routes where global Navbar + Footer should be hidden (fullscreen app pages)
const FULLSCREEN_ROUTES = [/^\/tools\/ai-communication-lab\/room\//];

const HideOnFullscreen: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  if (FULLSCREEN_ROUTES.some((re) => re.test(location.pathname))) return null;
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Router>
        <GoogleTagManager />
        <NoIndexGuard />
        <TrailingSlashRedirector />
        <PageViewTracker />
        <ToastContainer position="top-right" autoClose={3000} />
        <HideOnFullscreen><Navbar /></HideOnFullscreen>

        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route
              path="/courses"
              element={<Courses courses={courses} isImported={false} />}
            />
            <Route path="/contact" element={<Contact />} />
             <Route path="/scholarships" element={<GovernmentSchemesPage />} />
            <Route
              path="/course-details/:courseDisplayid/:courseid"
              element={<CourseDetails course={courses[0]} />}
            />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/career" element={<CareerPage />} />
            <Route path="/services/offline-class" element={<OfflineClass />} />
            <Route path="/services/online-class" element={<OnlineClass />} />
            <Route path="/services/project" element={<ProjectRequirement />} />
            <Route path="/resources/roadmap" element={<RoadmapPage />} />
            <Route path="/resources/roadmap/:path?" element={<RoadmapPage />} />
            <Route
              path="/resources/roadmap/:roadmap/step/:stepId"
              element={<RoadmapStepPage />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route
              path="/cancellation-refund-policy"
              element={<CancellationAndRefund />}
            />
            <Route
              path="/shipping-delivery-policy"
              element={<ShippingAndDelivery />}
            />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route
              path="/tutorials"
              element={<TutorialsPage tutorials={[]} isImported={false} />}
            />
            <Route
              path="/tutorials-details/:interviewDisplayId/:id/:questionSlug?"
              element={<TutorialsDetails />}
            />
            <Route path="/mentor" element={<Mentor />} />
            <Route
              path="/mentors"
              element={<AuthorsPage authors={authorList} isImported={false} />}
            />
            <Route
              path="/mentor-details/:name/:id"
              element={<AuthorDetails />}
            />
            <Route path="/search" element={<Search />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/dashboard/admin/tenants"
              element={<TenantAdminPage />}
            />
            <Route
              path="/dashboard/callrequest"
              element={<CallRequestsPage />}
            />
            <Route
              path="/dashboard/courseinquery"
              element={<CourseInqueryPage />}
            />
            <Route path="/dashboard/students" element={<StudentsPage />} />
            <Route path="/dashboard/mentors" element={<MentorPage />} />
            <Route path="/dashboard/courses" element={<CoursesPage />} />
            <Route path="/dashboard/orders/:status" element={<OrdersPage />} />
            <Route
              path="/dashboard/my-transactions"
              element={<MyTransactionsPage />}
            />
            <Route path="/profile" element={<Profile />} />
            <Route path="/course-counseling" element={<CourseCounseling />} />
            <Route
              path="/course-counseling-after-12th"
              element={<CourseCounselingAfter12th />}
            />
            <Route
              path="/course-counseling-after-graduation"
              element={<CourseCounselingAfterGraduation />}
            />
            <Route
              path="/online-course-counseling"
              element={<OnlineCourseCounseling />}
            />
            <Route
              path="/interviews"
              element={<InterviewsList isImported={false} />}
            />
            <Route
              path="/interview-details/:interviewDisplayId/:id"
              element={<InterviewDetails />}
            />
            <Route
              path="/interview-question/:questionDisplayId/:interview_id/:id"
              element={<InterviewQuestionDetails />}
            />
            <Route
              path="/dashboard/add-course/"
              element={<AddEditCoursePage />}
            />
            <Route
              path="/dashboard/edit-course/:courseDisplayid/:courseid"
              element={<AddEditCoursePage />}
            />
            <Route path="/dashboard/topics/:id" element={<TopicsPage />} />
            <Route
              path="/dashboard/add-topic/:courseid"
              element={<AddEditTopicPage />}
            />
            <Route
              path="/dashboard/edit-topic/:courseid/:id"
              element={<AddEditTopicPage />}
            />
            <Route
              path="/dashboard/bullet-points/:id"
              element={<BulletPointsPage />}
            />
            <Route
              path="/dashboard/add-bullet-point/:courseid"
              element={<AddEditBulletPointPage />}
            />
            <Route
              path="/dashboard/edit-bullet-point/:courseid/:id"
              element={<AddEditBulletPointPage />}
            />
            <Route
              path="/dashboard/course-for/:id"
              element={<CourseForPage />}
            />
            <Route
              path="/dashboard/add-course-for/:courseid"
              element={<AddEditCourseForPage />}
            />
            <Route
              path="/dashboard/edit-course-for/:courseid/:id"
              element={<AddEditCourseForPage />}
            />
            <Route
              path="/dashboard/perks-rewards/:id"
              element={<PerksRewardPage />}
            />
            <Route
              path="/dashboard/add-perk-reward/:courseid"
              element={<AddEditPerkRewardPage />}
            />
            <Route
              path="/dashboard/edit-perk-reward/:courseid/:id"
              element={<AddEditPerkRewardPage />}
            />
            <Route
              path="/dashboard/course-faq/:id"
              element={<CourseFaqPage />}
            />
            <Route
              path="/dashboard/add-course-faq/:courseid"
              element={<AddEditCourseFaqPage />}
            />
            <Route
              path="/dashboard/edit-course-faq/:courseid/:id"
              element={<AddEditCourseFaqPage />}
            />
            <Route
              path="/dashboard/course-resource/:id"
              element={<CourseResourcePage />}
            />
            <Route
              path="/dashboard/add-course-resource/:courseid"
              element={<AddEditCourseResourcesPage />}
            />
            <Route
              path="/dashboard/edit-course-resource/:courseid/:id"
              element={<AddEditCourseResourcesPage />}
            />
            <Route
              path="/dashboard/course-prerequisit/:id"
              element={<CoursePrerequisitPage />}
            />
            <Route
              path="/dashboard/add-course-prerequisit/:courseid"
              element={<AddEditCoursePrerequisitPage />}
            />
            <Route
              path="/dashboard/edit-course-prerequisit/:courseid/:id"
              element={<AddEditCoursePrerequisitPage />}
            />
            <Route
              path="/dashboard/course-quiz/:id"
              element={<CourseQuizPage />}
            />
            <Route
              path="/dashboard/add-course-quiz/:courseid"
              element={<AddEditCourseQuizPage />}
            />
            <Route
              path="/dashboard/edit-course-quiz/:courseid/:id"
              element={<AddEditCourseQuizPage />}
            />
            <Route
              path="/dashboard/interviews-tutorials/:type"
              element={<InterviewPage />}
            />
            <Route
              path="/dashboard/add-interview-or-tutorial/:type"
              element={<AddEditInterviewPage />}
            />
            <Route
              path="/dashboard/edit-interview-or-tutorial/:interviewDisplayid/:interview_id"
              element={<AddEditInterviewPage />}
            />
            <Route
              path="/dashboard/interview-or-tutorial-questions/:id"
              element={<InterviewQuestionPage />}
            />
            <Route
              path="/dashboard/add-interview-or-tutorial-question/:interview_id/:type"
              element={<AddEditInterviewQuestionPage />}
            />
            <Route
              path="/dashboard/edit-interview-or-tutorial-question/:interview_id/:id"
              element={<AddEditInterviewQuestionPage />}
            />
            <Route
              path="/dashboard/purchased-courses"
              element={<UserCourses />}
            />
            <Route
              path="/join-mentor"
              element={<MentorSignup isImported={false} />}
            />
            <Route
              path="/dashboard/add-mentor"
              element={<AddEditMentorPage />}
            />
            <Route
              path="/dashboard/update-mentor/:id"
              element={<AddEditMentorPage />}
            />
            <Route
              path="/dashboard/add-mentor-social/:id"
              element={<AddEditMentorSocialPage />}
            />
            <Route
              path="/dashboard/edit-mentor-social/:id/:social_id"
              element={<AddEditMentorSocialPage />}
            />
            <Route
              path="/dashboard/add-mentor-work/:id"
              element={<AddEditMentorWorkPage />}
            />
            <Route
              path="/dashboard/edit-mentor-work/:id/:work_id"
              element={<AddEditMentorWorkPage />}
            />
            <Route
              path="/mentor-earning-calculator"
              element={<MentorEarningCalculatorPage />}
            />
            <Route
              path="/course-details/qa/:id/:course_id"
              element={<QADetailPage />}
            />
            <Route path="/campus-counselor" element={<CampusCounselorPage />} />
            <Route
              path="/join-campus-counselor"
              element={<CampusCounselorSignup isImported={false} />}
            />
            <Route
              path="/dashboard/update-counselor/:id"
              element={<AddEditCounselorPage />}
            />
            <Route
              path="/dashboard/add-counselor-package/:counselor_id"
              element={<AddEditCounselorPackagePage />}
            />
            <Route
              path="/dashboard/edit-counselor-package/:id/:counselor_id"
              element={<AddEditCounselorPackagePage />}
            />
            <Route
              path="/dashboard/add-counselor-college/:id"
              element={<AddEditCounselorCollegePage />}
            />
            <Route
              path="/dashboard/edit-counselor-college/:id/:counselor_id"
              element={<AddEditCounselorCollegePage />}
            />
            <Route
              path="/dashboard/add-counselor-school/:id"
              element={<AddEditCounselorSchoolPage />}
            />
            <Route
              path="/dashboard/edit-counselor-school/:id/:counselor_id"
              element={<AddEditCounselorSchoolPage />}
            />
            <Route
              path="/campus-counselor-details/:id"
              element={<CounselorDetails />}
            />
            <Route path="/book-session/:id" element={<SessionBooking />} />
            <Route
              path="/dashboard/booked-session-for-me"
              element={<SessionBookedForMe />}
            />
            <Route
              path="/dashboard/booked-session-by-me"
              element={<SessionBookedByMe />}
            />
            <Route
              path="/dashboard/session/chat/:id"
              element={<ChatComponent />}
            />
            <Route
              path="/become-a-campus-counselor"
              element={<CampusCounselorIntro isImported={false} />}
            />
            <Route
              path="/tools/ai-communication-lab"
              element={<AiCommunicationLabPage />}
            />
            <Route
              path="/tools/ai-communication-lab/pricing"
              element={<AiCommunicationLabPricingPage />}
            />
            <Route
              path="/tools/ai-communication-lab/setup"
              element={<AiCommunicationLabSetupPage />}
            />
            <Route
              path="/tools/ai-communication-lab/room/:sessionId"
              element={<AiCommunicationLabRoomPage />}
            />
            <Route
              path="/pricing"
              element={<SessionPricing isImported={false} />}
            />
            <Route path="/api" element={<OpenApi />} />
            <Route
              path="/courses-after-graduation"
              element={<CoursesAfterGraduation />}
            />
            <Route path="/courses-after-12th" element={<CoursesAfter12th />} />
            <Route
              path="/career-change-courses"
              element={<CareerChangeCourses />}
            />
            <Route path="/career-roadmap" element={<CareerRoadmap />} />
            <Route
              path="/career-options-after-12th"
              element={<CareerOptionsAfter12th />}
            />
            <Route path="/career-counseling" element={<CareerCounseling />} />
            <Route
              path="/career-counseling-after-12th"
              element={<CareerCounselingAfter12th />}
            />
            <Route
              path="/career-counseling-after-graduation"
              element={<CareerCounselingAfterGraduation />}
            />
            <Route
              path="/career-counseling-career-change"
              element={<CareerCounselingCareerChange />}
            />
            <Route
              path="/education-counseling"
              element={<EducationCounseling />}
            />
            <Route path="/free-resources" element={<FreeResources />} />
            <Route path="/dashboard/ai-widget" element={<AiWidgetPage />} />
            <Route path="/products" element={<Products />} />
            <Route path="/ai-plugin" element={<AiPlugin />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/docs/api" element={<Docs />} />
            <Route path="/docs/config" element={<Docs />} />
            <Route path="/docs/examples" element={<Docs />} />
            <Route path="/docs/vanilla" element={<Docs />} />
            <Route
              path="/ai-sales-assistant-for-website"
              element={<AiSalesAssistant />}
            />
            <Route
              path="/ai-chatbot-for-website"
              element={<AiChatbotForWebsite />}
            />
            <Route
              path="/increase-website-conversions"
              element={<IncreaseWebsiteConversions />}
            />
            {/* Personal & Family Growth */}
            {/* Interactive Tools — SEO Traffic Drivers */}
            <Route path="/tools" element={<ToolsHubPage />} />
            <Route path="/tools/upgrade" element={<ToolsUpgradePage />} />
            <Route
              path="/tools/communication-checker"
              element={<CommunicationCheckerPage />}
            />
            <Route
              path="/tools/confidence-score-test"
              element={<ConfidenceScoreTestPage />}
            />
            <Route
              path="/tools/email-writing-analyzer"
              element={<EmailWritingAnalyzerPage />}
            />
            <Route
              path="/tools/stress-level-quiz"
              element={<StressLevelQuizPage />}
            />
            <Route path="/tools/iq-test" element={<IQTestPage />} />
            <Route
              path="/tools/financial-planning-sip-calculator"
              element={<FinancialPlanningSipCalculatorPage />}
            />
            <Route
              path="/tools/salary-growth-predictor"
              element={<SalaryGrowthPredictorPage />}
            />
            <Route
              path="/tools/career-blueprint-generator"
              element={<CareerBlueprintGeneratorPage />}
            />
            <Route
              path="/government-schemes"
              element={<GovernmentSchemesPage />}
            />
            <Route
              path="/government-schemes/:stateSlug"
              element={<GovernmentSchemesPage />}
            />
            <Route
              path="/government-schemes/details/:slug"
              element={<SchemeDetailsPage />}
            />
            <Route
              path="/government-schemes/eligibility-checker"
              element={<EligibilityCheckerPage />}
            />
            <Route
              path="/scholarships-for-engineering-students"
              element={<GovernmentSchemesPage />}
            />
            <Route
              path="/government-schemes-for-girls"
              element={<GovernmentSchemesPage />}
            />
            <Route path="/saved-schemes" element={<GovernmentSchemesPage />} />
            <Route path="/life-skills" element={<LifeSkills />} />
            <Route
              path="/improve-communication-skills"
              element={<ImproveCommunicationSkills />}
            />
            <Route
              path="/life-skills/program/:slug"
              element={<ProgramDetailsPage />}
            />
            <Route
              path="/life-skills/program/:slug/lesson/:lessonId"
              element={<LessonPage />}
            />
            <Route
              path="/subscriptions/:subscriptionId/program/:programId"
              element={<SubscriptionManagementPage />}
            />
            {/* Find Services - Professional Services */}
            <Route path="/find-services" element={<FindServices />} />
            <Route
              path="/find-services/:categorySlug/list"
              element={<FindServicesList />}
            />
            <Route
              path="/find-services/:categorySlug/provider/:providerSlug"
              element={<ServiceProviderDetail />}
            />
            <Route path="/become-provider" element={<ProviderOnboarding />} />
            {/* Life Skills Admin - Content Management */}
            <Route
              path="/dashboard/life-skills/programs"
              element={<LifeSkillsProgramsPage />}
            />
            <Route
              path="/dashboard/ai-communication-lab"
              element={<AiCommunicationLabAdminPage />}
            />
            <Route
              path="/dashboard/ai-communication-lab/transactions"
              element={<AiCommunicationLabTransactionsPage />}
            />
            <Route
              path="/tools/ai-communication-lab"
              element={<AiCommunicationLabPage />}
            />
            <Route
              path="/tools/ai-communication-lab/pricing"
              element={<AiCommunicationLabPricingPage />}
            />
            <Route
              path="/dashboard/life-skills/categories"
              element={<LifeSkillsCategoriesPage />}
            />
            <Route
              path="/dashboard/life-skills/categories/add"
              element={<AddEditLifeSkillsCategoryPage />}
            />
            <Route
              path="/dashboard/life-skills/categories/edit/:id"
              element={<AddEditLifeSkillsCategoryPage />}
            />
            <Route
              path="/dashboard/life-skills/pricing"
              element={<LifeSkillsPricingPage />}
            />
            <Route
              path="/dashboard/life-skills/pricing/add"
              element={<AddEditLifeSkillsPricingPage />}
            />
            <Route
              path="/dashboard/life-skills/pricing/edit/:id"
              element={<AddEditLifeSkillsPricingPage />}
            />
            <Route
              path="/dashboard/life-skills/activities"
              element={<LifeSkillsActivitiesPage />}
            />
            <Route
              path="/dashboard/life-skills/activities/add"
              element={<AddEditLifeSkillsActivityPage />}
            />
            <Route
              path="/dashboard/life-skills/activities/edit/:id"
              element={<AddEditLifeSkillsActivityPage />}
            />
            <Route
              path="/dashboard/life-skills/subscriptions"
              element={<LifeSkillsSubscriptionsPage />}
            />
            {/* Life Skills - Client Management */}
            <Route
              path="/dashboard/life-skills/clients"
              element={<LifeSkillsClientsPage />}
            />
            <Route
              path="/dashboard/life-skills/clients/add"
              element={<AddEditClientPage />}
            />
            <Route
              path="/dashboard/life-skills/clients/edit/:id"
              element={<AddEditClientPage />}
            />
            <Route
              path="/dashboard/life-skills/clients/:id"
              element={<ClientDetailPage />}
            />
            <Route
              path="/dashboard/life-skills/progress-logs"
              element={<LifeSkillsProgressLogsPage />}
            />
            <Route
              path="/dashboard/life-skills/toy-catalog"
              element={<LifeSkillsToyCatalogPage />}
            />
            <Route
              path="/dashboard/life-skills/toy-catalog/add"
              element={<AddEditToyCatalogPage />}
            />
            <Route
              path="/dashboard/life-skills/toy-catalog/edit/:id"
              element={<AddEditToyCatalogPage />}
            />
            <Route
              path="/dashboard/life-skills/diet-plans"
              element={<LifeSkillsDietPlansPage />}
            />
            <Route
              path="/dashboard/life-skills/diet-plans/add"
              element={<AddEditDietPlanPage />}
            />
            <Route
              path="/dashboard/life-skills/diet-plans/edit/:id"
              element={<AddEditDietPlanPage />}
            />
            <Route
              path="/dashboard/life-skills/partner-centers"
              element={<LifeSkillsPartnerCentersPage />}
            />
            <Route
              path="/dashboard/life-skills/partner-centers/add"
              element={<AddEditPartnerCenterPage />}
            />
            <Route
              path="/dashboard/life-skills/partner-centers/edit/:id"
              element={<AddEditPartnerCenterPage />}
            />
            {/* Parent Portal */}
            <Route path="/parent/login" element={<Login />} />
            <Route
              path="/parent/dashboard"
              element={
                <ParentPrivateRoute>
                  <ParentDashboard />
                </ParentPrivateRoute>
              }
            />
            <Route
              path="/parent/progress/new"
              element={
                <ParentPrivateRoute>
                  <ParentProgressForm />
                </ParentPrivateRoute>
              }
            />
            <Route
              path="/parent/recommendations"
              element={
                <ParentPrivateRoute>
                  <ParentRecommendations />
                </ParentPrivateRoute>
              }
            />
            {/* HTML Sitemap — improves crawl discovery and reduces orphan pages */}
            <Route path="/sitemap" element={<HtmlSitemap />} />
            <Route path="*" element={<NotFound />} /> {/* Catch-all for 404 */}
            {/* ── Blog — Public ── */}
            <Route path="/blog" element={<BlogListingPage />} />
            <Route path="/blog/write" element={<GuestBlogWritePage />} />
            <Route
              path="/blog/subscription"
              element={<BlogSubscriptionPage />}
            />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            {/* ── Blog — Admin ── */}
            <Route element={<PrivateRoute allowedClaims={["SUPERADMIN", "ADMIN"]} />}>
              <Route path="/dashboard/blog" element={<AdminBlogListPage />} />
              <Route
                path="/dashboard/blog/new"
                element={<AdminBlogEditorPage />}
              />
              <Route
                path="/dashboard/blog/edit/:id"
                element={<AdminBlogEditorPage />}
              />
              <Route
                path="/dashboard/newsletter"
                element={<AdminNewsletterPage />}
              />
              <Route
                path="/dashboard/blog-suggestions"
                element={<AdminBlogSuggestionsPage />}
              />
              <Route
                path="/dashboard/government-schemes"
                element={<GovernmentSchemesAdminPage />}
              />
              <Route
                path="/dashboard/government-schemes/new"
                element={<AddEditGovernmentSchemePage />}
              />
              <Route
                path="/dashboard/government-schemes/edit/:id"
                element={<AddEditGovernmentSchemePage />}
              />
              <Route
                path="/dashboard/search-analytics"
                element={<SearchAnalyticsPage />}
              />
              <Route
                path="/dashboard/activity-analytics"
                element={<ActivityDashboard />}
              />
            </Route>
          </Routes>
        </Suspense>

        <Suspense fallback={null}>
          <HideOnFullscreen><Footer /></HideOnFullscreen>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
};

const TrailingSlashRedirector = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search, hash } = location;

    // 1. Lowercase check — /Course-Details/XYZ → /course-details/xyz
    const lowered = pathname.toLowerCase();
    if (pathname !== lowered) {
      navigate(lowered + search + hash, { replace: true });
      return;
    }

    // 2. Trailing slash — /about → /about/
    if (pathname !== "/" && !pathname.endsWith("/")) {
      const normalizedPath = normalizePath(pathname);
      navigate(normalizedPath + search + hash, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

export default App;
